import React from 'react';
import { Modal } from '../../shared/components/Modal';
import { FlaskConical, FileText, Files, LayoutTemplate, Sparkles, BookOpen } from 'lucide-react';
import { NavRoute } from '../../components/layout/Sidebar';

interface QuickCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectType: (route: NavRoute) => void;
}

export const QuickCreateModal: React.FC<QuickCreateModalProps> = ({
  isOpen,
  onClose,
  onSelectType,
}) => {
  const documentTypes = [
    {
      id: 'report-studio' as NavRoute,
      title: 'Laboratory Report',
      description: 'Structured academic report with theory, experimental data, calculations, and KaTeX equations.',
      icon: <FlaskConical size={22} color="var(--primary-600)" />,
      badge: 'Most Popular',
    },
    {
      id: 'cover-studio' as NavRoute,
      title: 'Cover Page Studio',
      description: 'Institutional cover page for assignments, lab reports, term papers, and thesis submissions.',
      icon: <FileText size={22} color="var(--color-info)" />,
    },
    {
      id: 'report-studio' as NavRoute,
      title: 'Coursework / Assignment',
      description: 'Clean modular document with customized sections, problem sets, and solutions.',
      icon: <BookOpen size={22} color="var(--color-warning)" />,
    },
    {
      id: 'document-studio' as NavRoute,
      title: 'PDF & Page Studio',
      description: 'Merge, split, rotate, reorder, or assemble multi-page PDF documents locally.',
      icon: <Files size={22} color="var(--color-success)" />,
    },
    {
      id: 'templates' as NavRoute,
      title: 'Browse Template Gallery',
      description: 'Start from pre-configured department-approved templates and formatting presets.',
      icon: <LayoutTemplate size={22} color="var(--primary-700)" />,
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Academic Document"
      maxWidth="620px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          Choose a document type to open its dedicated studio workspace:
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--space-3)' }}>
          {documentTypes.map((item, idx) => (
            <div
              key={idx}
              className="card card-interactive"
              onClick={() => onSelectType(item.id)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                padding: 'var(--space-4)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </div>
                {item.badge && (
                  <span className="badge badge-primary" style={{ fontSize: '10px' }}>
                    <Sparkles size={10} /> {item.badge}
                  </span>
                )}
              </div>
              <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                {item.title}
              </h4>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
